import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { Upload, Plus, X, Loader2, ArrowLeft, Music } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { RELEASE_TYPES } from '@/constants/releaseTypes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VERSION_OPTIONS = ['Original Mix', 'Extended Mix', 'Radio Edit', 'Club Mix', 'VIP Mix', 'Instrumental', 'Acapella', 'Remix', 'Bootleg', 'Mashup'];
const FILE_TYPE_OPTIONS = ['MP3 320', 'MP3 256', 'WAV', 'WAV 24bit', 'FLAC', 'AIFF'];

const AdminEditReleasePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [genres, setGenres] = useState([]);
  const [album, setAlbum] = useState(null);

  const [formData, setFormData] = useState({
    title: '', artist: '', release_type: 'exclusive', genre_ids: [], price: '', description: '',
  });
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState('');
  const [existingCover, setExistingCover] = useState('');
  const [tracks, setTracks] = useState([]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchData = async () => {
    try {
      const [albumRes, genresRes] = await Promise.all([
        axios.get(`${API}/albums/${id}`),
        axios.get(`${API}/genres`),
      ]);
      const a = albumRes.data;
      setAlbum(a);
      setGenres(genresRes.data);
      setFormData({
        title: a.title || '',
        artist: a.artist || '',
        release_type: a.release_type || 'exclusive',
        genre_ids: a.genre_ids || (a.genre_id ? [a.genre_id] : []),
        price: String(a.price ?? ''),
        description: a.description || '',
        legacy_slugs: (a.legacy_slugs || []).join('\n'),
      });
      setExistingCover(a.cover_url);
      setCoverPreview(a.cover_url?.startsWith('http') ? a.cover_url : `${BACKEND_URL}${a.cover_url}`);
      setTracks((a.tracks || []).map(t => ({
        id: t.id,
        title: t.title || '',
        duration: t.duration || '',
        bpm: t.bpm != null ? String(t.bpm) : '',
        key: t.key || '',
        version: t.version || 'Original Mix',
        file_type: t.file_type || 'MP3 320',
        preview_url: t.preview_url,
        full_file_path: t.full_file_path,
        preview_file: null,   // new file (replacement)
        full_file: null,      // new file (replacement)
      })));
    } catch (e) {
      toast.error('Failed to load release');
      navigate('/admin/releases');
    } finally {
      setLoading(false);
    }
  };

  const uploadFile = async (file, endpoint, fileType) => {
    const fd = new FormData();
    fd.append('file', file);
    if (endpoint.includes('audio')) fd.append('file_type', fileType);
    const r = await axios.post(`${API}${endpoint}`, fd);
    // { url: "/api/files/dj-music-store/...", path: "dj-music-store/..." }
    return r.data;
  };

  const addTrack = () => {
    setTracks([...tracks, {
      id: undefined, title: '', duration: '3:30', bpm: '', key: '',
      version: 'Original Mix', file_type: 'MP3 320',
      preview_url: '', full_file_path: '', preview_file: null, full_file: null,
    }]);
  };

  const removeTrack = (idx) => setTracks(tracks.filter((_, i) => i !== idx));

  const handleCoverChange = (e) => {
    const f = e.target.files[0];
    if (f) {
      setCoverFile(f);
      setCoverPreview(URL.createObjectURL(f));
    }
  };

  const setTrackField = (idx, field, value) => {
    const next = [...tracks];
    next[idx][field] = value;
    setTracks(next);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Cover: upload new if provided, else keep
      let cover_url = existingCover;
      if (coverFile) {
        const resp = await uploadFile(coverFile, '/upload/cover');
        cover_url = resp.url;
      }

      // Tracks: for each, upload new preview/full if provided, else keep
      const updatedTracks = [];
      for (const t of tracks) {
        let preview_url = t.preview_url;
        let full_file_path = t.full_file_path;

        if (t.preview_file) {
          const resp = await uploadFile(t.preview_file, '/upload/audio', 'preview');
          preview_url = `${BACKEND_URL}${resp.url}`;
        }
        if (t.full_file) {
          const resp = await uploadFile(t.full_file, '/upload/audio', 'full');
          full_file_path = resp.path;  // BARE storage path, NOT the /api/files/... url
        }

        // Require preview + full for any new track added during editing
        if (!preview_url || !full_file_path) {
          throw new Error(`Track "${t.title || 'untitled'}" is missing preview or full audio.`);
        }

        updatedTracks.push({
          id: t.id,
          title: t.title,
          duration: t.duration,
          preview_url,
          full_file_path,
          bpm: t.bpm ? parseInt(t.bpm) : null,
          key: t.key || null,
          version: t.version || null,
          file_type: t.file_type || null,
        });
      }

      const payload = {
        id: album.id,
        title: formData.title,
        artist: formData.artist,
        cover_url,
        release_type: formData.release_type,
        genre_ids: formData.genre_ids,
        price: parseFloat(formData.price),
        description: formData.description || null,
        tracks: updatedTracks,
        created_at: album.created_at,
      };

      await axios.put(`${API}/albums/${album.id}`, payload);
      // Save legacy Shopify slugs — separate endpoint so we can 409 on collisions
      const rawSlugs = (formData.legacy_slugs || '').split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
      try {
        await axios.put(`${API}/admin/albums/${album.id}/legacy-slugs`, { slugs: rawSlugs });
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'One of the legacy URLs is already linked to another release.');
        // Don't unwind the release save — the album is still updated.
      }
      toast.success('Release updated!');
      setTimeout(() => navigate('/admin/home'), 800);
    } catch (err) {
      toast.error(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <Loader2 className="text-[#00F0FF] animate-spin" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Music className="text-[#00F0FF]" size={28} />
            <div>
              <h1 className="text-2xl font-heading font-bold" data-testid="edit-release-title">Edit Release</h1>
              <p className="text-sm text-[#A1A1AA]">{album?.title}</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/admin/releases')}
            className="border-[#27272A] hover:bg-[#18181B] rounded-full"
            data-testid="back-to-releases-button"
          >
            <ArrowLeft size={16} className="mr-2" /> Back
          </Button>
        </div>

        <form onSubmit={handleSave} className="bg-[#121214] border border-[#27272A] rounded-lg p-8 space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <h2 className="text-xl font-heading font-semibold">Basic Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title *</label>
                <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required className="bg-[#18181B] border-[#27272A]" data-testid="input-title" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Artist *</label>
                <Input value={formData.artist} onChange={(e) => setFormData({ ...formData, artist: e.target.value })} required className="bg-[#18181B] border-[#27272A]" data-testid="input-artist" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Release Type *</label>
                <select value={formData.release_type} onChange={(e) => setFormData({ ...formData, release_type: e.target.value })} className="w-full h-10 px-3 bg-[#18181B] border border-[#27272A] rounded-md text-white" data-testid="select-release-type">
                  {RELEASE_TYPES.map(rt => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Price (₱) *</label>
                <Input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} required className="bg-[#18181B] border-[#27272A]" data-testid="input-price" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Selected genres</label>
                <div className="h-10 px-3 flex items-center bg-[#18181B] border border-[#27272A] rounded-md text-sm text-[#A1A1AA]">
                  {formData.genre_ids.length} selected
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Genres * (click to toggle)</label>
              <div className="flex flex-wrap gap-2 p-3 bg-[#18181B] border border-[#27272A] rounded-md min-h-[52px]" data-testid="genre-multiselect">
                {genres.map(g => {
                  const active = formData.genre_ids.includes(g.id);
                  return (
                    <button
                      type="button"
                      key={g.id}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        genre_ids: prev.genre_ids.includes(g.id)
                          ? prev.genre_ids.filter(id => id !== g.id)
                          : [...prev.genre_ids, g.id],
                      }))}
                      className={`px-3 py-1 rounded-full text-sm transition-colors ${active ? 'bg-[#00F0FF] text-black' : 'bg-[#09090B] text-[#A1A1AA] border border-[#27272A] hover:text-white'}`}
                      data-testid={`toggle-genre-${g.slug}`}
                    >
                      {g.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Cover */}
          <div>
            <h2 className="text-xl font-heading font-semibold mb-4">Cover Image</h2>
            <div className="flex gap-4 items-start">
              {coverPreview && <img src={coverPreview} alt="Cover" className="w-32 h-32 rounded-lg object-cover" />}
              <div className="flex-1">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-[#27272A] rounded-lg cursor-pointer hover:border-[#00F0FF] transition-colors">
                  <Upload className="mb-2 text-[#A1A1AA]" size={28} />
                  <span className="text-sm text-[#A1A1AA]">{coverFile ? `New: ${coverFile.name}` : 'Click to replace cover image'}</span>
                  <input type="file" accept="image/*" onChange={handleCoverChange} className="hidden" data-testid="input-cover" />
                </label>
              </div>
            </div>
          </div>

          {/* Legacy Shopify URLs — redirect old social-media links here */}
          <div>
            <h2 className="text-xl font-heading font-semibold mb-1">Legacy Shopify URLs</h2>
            <p className="text-xs text-[#71717A] mb-3">
              Paste any old Shopify URL (or just its /products/... slug) that used to link to this release. Any visitor hitting it will be redirected to /release/{album?.id}. One per line — full URLs or bare slugs both work.
            </p>
            <textarea
              value={formData.legacy_slugs}
              onChange={(e) => setFormData({ ...formData, legacy_slugs: e.target.value })}
              rows={3}
              placeholder="https://www.kehelekeffmusic.com/products/vipmashuppackvol92025"
              className="w-full bg-[#18181B] border border-[#27272A] rounded-md px-3 py-2 text-sm font-mono placeholder:text-[#3F3F46]"
              data-testid="input-legacy-slugs"
            />
          </div>

          {/* Tracks */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-heading font-semibold">Tracks</h2>
              <Button type="button" onClick={addTrack} variant="outline" size="sm" className="border-[#27272A]" data-testid="add-track-button">
                <Plus size={16} className="mr-2" /> Add Track
              </Button>
            </div>
            <div className="space-y-4">
              {tracks.map((track, index) => (
                <div key={index} className="bg-[#18181B] border border-[#27272A] rounded-lg p-4 space-y-3" data-testid={`track-editor-${index}`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium">Track {index + 1}</h3>
                    {tracks.length > 1 && (
                      <button type="button" onClick={() => removeTrack(index)} className="text-[#A1A1AA] hover:text-[#EF4444]" data-testid={`remove-track-${index}`}>
                        <X size={20} />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-[#A1A1AA] mb-1">Track Title *</label>
                      <Input value={track.title} onChange={(e) => setTrackField(index, 'title', e.target.value)} required className="bg-[#09090B] border-[#27272A]" data-testid={`input-track-title-${index}`} />
                    </div>
                    <div>
                      <label className="block text-xs text-[#A1A1AA] mb-1">Duration *</label>
                      <Input value={track.duration} onChange={(e) => setTrackField(index, 'duration', e.target.value)} required className="bg-[#09090B] border-[#27272A]" data-testid={`input-track-duration-${index}`} />
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs text-[#A1A1AA] mb-1">BPM</label>
                      <Input value={track.bpm} onChange={(e) => setTrackField(index, 'bpm', e.target.value)} className="bg-[#09090B] border-[#27272A]" data-testid={`input-track-bpm-${index}`} />
                    </div>
                    <div>
                      <label className="block text-xs text-[#A1A1AA] mb-1">Key</label>
                      <Input value={track.key} onChange={(e) => setTrackField(index, 'key', e.target.value)} className="bg-[#09090B] border-[#27272A]" data-testid={`input-track-key-${index}`} />
                    </div>
                    <div>
                      <label className="block text-xs text-[#A1A1AA] mb-1">Version</label>
                      <select value={track.version} onChange={(e) => setTrackField(index, 'version', e.target.value)} className="w-full h-10 px-2 bg-[#09090B] border border-[#27272A] rounded-md text-white text-sm" data-testid={`select-track-version-${index}`}>
                        {VERSION_OPTIONS.map(o => <option key={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-[#A1A1AA] mb-1">File Type</label>
                      <select value={track.file_type} onChange={(e) => setTrackField(index, 'file_type', e.target.value)} className="w-full h-10 px-2 bg-[#09090B] border border-[#27272A] rounded-md text-white text-sm" data-testid={`select-track-filetype-${index}`}>
                        {FILE_TYPE_OPTIONS.map(o => <option key={o}>{o}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-[#A1A1AA] mb-1">
                        Preview Audio {track.preview_url ? <span className="text-[#10B981]">✓ current file kept</span> : '*'}
                      </label>
                      <Input type="file" accept="audio/*" onChange={(e) => setTrackField(index, 'preview_file', e.target.files[0])} className="bg-[#09090B] border-[#27272A] text-sm" data-testid={`input-track-preview-${index}`} />
                      {track.preview_file && <p className="text-xs text-[#00F0FF] mt-1">Will replace with: {track.preview_file.name}</p>}
                    </div>
                    <div>
                      <label className="block text-xs text-[#A1A1AA] mb-1">
                        Full Audio {track.full_file_path ? <span className="text-[#10B981]">✓ current file kept</span> : '*'}
                      </label>
                      <Input type="file" accept="audio/*" onChange={(e) => setTrackField(index, 'full_file', e.target.files[0])} className="bg-[#09090B] border-[#27272A] text-sm" data-testid={`input-track-full-${index}`} />
                      {track.full_file && <p className="text-xs text-[#00F0FF] mt-1">Will replace with: {track.full_file.name}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="submit" disabled={saving} className="flex-1 bg-[#00F0FF] hover:bg-[#33F3FF] text-black font-medium rounded-full h-12" data-testid="save-release-button">
              {saving ? <><Loader2 className="mr-2 animate-spin" size={20} /> Saving...</> : 'Save Changes'}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate('/admin/releases')} className="border-[#27272A] hover:bg-[#18181B] rounded-full">Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminEditReleasePage;
